import { useEffect, useState } from "react";
import { browser } from "wxt/browser";
import type { ApprovalRequest, PolicyStore, ToolCall } from "./guard";
import { type AgentMode, DEFAULT_MODE, isAgentMode, isSiteRule, type SiteRule } from "./policy";

/**
 * The browser agent's permissions in Chrome's storage (ADMIN-220):
 * - `chrome.storage.local`: each chat's mode (`agent-mode:<thread>`) and the site rules
 *   (`agent-site-rules`), kept across restarts and shared by the panel, the new tab and the worker.
 * - `chrome.storage.session`: "allow on this site for this chat" grants and calls waiting for an
 *   answer, gone when the browser closes.
 */

const RULES_KEY = "agent-site-rules";
const modeKey = (threadId: string) => `agent-mode:${threadId}`;
const grantsKey = (threadId: string) => `agent-grants:${threadId}`;
const pendingKey = (toolCallId: string) => `agent-pending:${toolCallId}`;

async function readRules(): Promise<Record<string, SiteRule>> {
  const stored = (await browser.storage.local.get(RULES_KEY))[RULES_KEY];
  if (!stored || typeof stored !== "object") return {};
  return Object.fromEntries(
    Object.entries(stored as Record<string, unknown>).filter((entry): entry is [string, SiteRule] =>
      isSiteRule(entry[1]),
    ),
  );
}

async function getAgentMode(threadId: string): Promise<AgentMode> {
  const stored = (await browser.storage.local.get(modeKey(threadId)))[modeKey(threadId)];
  return isAgentMode(stored) ? stored : DEFAULT_MODE;
}

async function setAgentMode(threadId: string, mode: AgentMode) {
  await browser.storage.local.set({ [modeKey(threadId)]: mode });
}

/** Sets a site's rule, or removes it with null. */
export async function setSiteRule(site: string, rule: SiteRule | null) {
  const rules = await readRules();
  if (rule) rules[site] = rule;
  else delete rules[site];
  await browser.storage.local.set({ [RULES_KEY]: rules });
}

export const chromePolicyStore: PolicyStore = {
  mode: getAgentMode,
  rules: readRules,
  async granted(threadId, site) {
    const grants = (await browser.storage.session.get(grantsKey(threadId)))[grantsKey(threadId)];
    return Array.isArray(grants) && grants.includes(site);
  },
  async grant(threadId, site) {
    const key = grantsKey(threadId);
    const grants = (await browser.storage.session.get(key))[key];
    const next = new Set(Array.isArray(grants) ? (grants as string[]) : []);
    next.add(site);
    await browser.storage.session.set({ [key]: [...next] });
  },
  async savePending(call: ToolCall, request: ApprovalRequest) {
    await browser.storage.session.set({ [pendingKey(call.toolCallId)]: { call, request } });
  },
  async takePending(toolCallId) {
    const key = pendingKey(toolCallId);
    const stored = (await browser.storage.session.get(key))[key];
    await browser.storage.session.remove(key);
    return (stored as { call: ToolCall; request: ApprovalRequest } | undefined) ?? null;
  },
};

/** A chat's mode, live (the picker and the worker read the same key). */
export function useAgentMode(threadId: string | undefined): [AgentMode, (mode: AgentMode) => void] {
  const [mode, setMode] = useState<AgentMode>(DEFAULT_MODE);
  useEffect(() => {
    if (!threadId) return;
    let live = true;
    void getAgentMode(threadId).then((m) => live && setMode(m));
    const onChanged = (changes: Record<string, { newValue?: unknown }>, area: string) => {
      const change = changes[modeKey(threadId)];
      if (area === "local" && change) {
        setMode(isAgentMode(change.newValue) ? change.newValue : DEFAULT_MODE);
      }
    };
    browser.storage.onChanged.addListener(onChanged);
    return () => {
      live = false;
      browser.storage.onChanged.removeListener(onChanged);
    };
  }, [threadId]);
  const update = (next: AgentMode) => {
    setMode(next);
    if (threadId) void setAgentMode(threadId, next);
  };
  return [mode, update];
}

/** The site rules, live, for Settings. */
export function useSiteRules(): Record<string, SiteRule> {
  const [rules, setRules] = useState<Record<string, SiteRule>>({});
  useEffect(() => {
    let live = true;
    void readRules().then((r) => live && setRules(r));
    const onChanged = (changes: Record<string, unknown>, area: string) => {
      if (area === "local" && RULES_KEY in changes)
        void readRules().then((r) => live && setRules(r));
    };
    browser.storage.onChanged.addListener(onChanged);
    return () => {
      live = false;
      browser.storage.onChanged.removeListener(onChanged);
    };
  }, []);
  return rules;
}
